import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Flame,
  GraduationCap,
  HeartPulse,
  History,
  Landmark,
  LineChart,
  MessagesSquare,
  Scale,
  ShieldAlert,
  Sparkles,
  SunMedium,
  UserPlus,
  UserRound,
  Users,
  UsersRound,
  Wrench,
} from "lucide-react";
import type { ChatEntry } from "@/types/chat";
import {
  HUB_GATED_CHIP_IDS,
  isMentorEntryBlockedForPersona,
  resolveChatEntryForPersona,
  type WorkspacePersona,
} from "@/config/workspacePersona";

/**
 * A "recommendation" is a one-tap navigation we suggest to the user inside the
 * open chat ("looks like you're working on a behavior issue — try the Behavior
 * Incident Toolkit"). Two kinds:
 *  - `tool`: structured workflow page (Behavior Incident Toolkit, Strategic Decision Partner)
 *  - `chat`: focused chat space (Reset, Reclaim, Connect with Students, …)
 */
export type ChatRecommendationKind = "tool" | "chat";

export type ChatRecommendationDefinition = {
  /** Stable id used for analytics, server hints, and persona gating. */
  id: string;
  /** Maps to `SidebarNavItem.id` in `sidebarNav.ts` for coverage tests. */
  sidebarNavItemId: string;
  kind: ChatRecommendationKind;
  /** Short button-style label. Keep ≤ 32 chars. */
  label: string;
  /** One-sentence blurb shown under the label in the card. Keep ≤ 120 chars. */
  blurb: string;
  icon: LucideIcon;
  /** For `kind === "tool"`: the route to push. */
  route?: string;
  /** For `kind === "chat"`: the chat entry to open via `navigateToChatEntryForApp`. */
  chatEntry?: ChatEntry;
  /**
   * Lowercased trigger phrases. The matcher counts the number of distinct
   * triggers found in the user's message; longer phrases beat shorter ones,
   * and `tool` recommendations beat `chat` recommendations on a tie because
   * structured workflows tend to be the higher-value next step.
   */
  triggers: string[];
  /**
   * Phrases that count as the user *explicitly* asking for this destination
   * (e.g. typing the literal tool name). When any of these appear in a user
   * message, the auto-suggest "seen once" suppression is bypassed and the
   * card pops again. Keep these unambiguous — they should only fire when the
   * user clearly knows what they're asking for.
   */
  explicitAskTriggers: string[];
  /** Show as a quick chip on the hub hero empty state. */
  showInHubHero?: boolean;
  /** Lower numbers appear first on the hub hero. */
  hubChipOrder?: number;
  /** Chip label override (defaults to trimmed `label`). */
  hubChipLabel?: string;
  /** Example phrase shown in hub input helper text. */
  hubHintExample?: string;
};

const LEADER_ONLY_REC_IDS = new Set([
  "navigate_board_relationships",
  "navigate_parent_relationships",
  "leading_your_staff",
  "renew_my_spark",
  "my_teams",
  "invite_teammates",
]);

const NON_LEADER_REC_IDS = new Set(["strengthen_my_voice", "lead_with_clarity"]);

/**
 * Single source of truth for "where chat can send the user next." Order
 * matters only for stable iteration (the matcher scores by trigger weight).
 *
 * To add a destination: add a row here, give it a unique `id`, set the
 * `route` or `chatEntry`, and write a focused list of triggers (verbs +
 * nouns the user is likely to type, including misspellings worth catching).
 */
export const CHAT_RECOMMENDATIONS: ChatRecommendationDefinition[] = [
  {
    id: "behavior_toolkit",
    sidebarNavItemId: "behavior-incident",
    kind: "tool",
    label: "Open the Behavior Incident Toolkit",
    blurb: "Walk through the incident step by step and capture documentation you can save.",
    icon: ShieldAlert,
    route: "/restoration-room",
    triggers: [
      "behavior",
      "behaviour",
      "behavior incident",
      "behavior issue",
      "behaviour issue",
      "behavior problem",
      "discipline",
      "disciplinary",
      "detention",
      "in-school suspension",
      "in school suspension",
      "out of school suspension",
      "out-of-school suspension",
      "iss ",
      "oss ",
      "fight",
      "fighting",
      "altercation",
      "hit a student",
      "hit another student",
      "kicked",
      "punched",
      "shoved",
      "bullying",
      "outburst",
      "meltdown",
      "tantrum",
      "disruptive",
      "disrupting",
      "office referral",
      "referral",
      "suspension",
      "suspend",
      "suspended",
      "expulsion",
      "expel",
      "expelled",
      "restorative",
      "restorative justice",
      "restorative practice",
      "incident report",
      "wrote up",
      "write up",
      "code of conduct",
    ],
    explicitAskTriggers: [
      "behavior incident toolkit",
      "behaviour incident toolkit",
      "open the behavior incident toolkit",
      "use the behavior incident toolkit",
      "incident toolkit",
    ],
    showInHubHero: true,
    hubChipOrder: 8,
    hubChipLabel: "Behavior incident toolkit",
    hubHintExample: "behavior incident",
  },
  {
    id: "strategic_decision",
    sidebarNavItemId: "think-decision",
    kind: "tool",
    label: "Open the Strategic Decision Partner",
    blurb: "Move from tension → evidence → 30/60/90 plan → documentation you can revisit.",
    icon: Wrench,
    route: "/tools/think-through-decision",
    triggers: [
      "decision",
      "decisions",
      "strategy",
      "strategic",
      "strategic decision",
      "make a decision",
      "decide between",
      "help me figure this out",
      "figure this out",
      "figure out what to do",
      "plan out",
      "plan this out",
      "thinking through",
      "think through",
      "think this through",
      "weighing",
      "weigh the",
      "trade-off",
      "tradeoff",
      "trade off",
      "should i",
      "torn between",
      "stuck on a decision",
      "stuck on this",
      "implementation plan",
      "30 60 90",
      "30/60/90",
      "effect size",
      "evidence-based",
      "evidence based",
      "research-based",
      "research based",
      "school improvement plan",
      "policy decision",
      "budget decision",
      "staffing decision",
    ],
    explicitAskTriggers: [
      "strategic decision partner",
      "open the strategic decision partner",
      "use the strategic decision partner",
      "decision partner",
    ],
    showInHubHero: true,
    hubChipOrder: 7,
    hubChipLabel: "Strategic Decision Partner",
    hubHintExample: "think through a decision",
  },
  {
    id: "teacher_scenarios",
    sidebarNavItemId: "teacher-scenarios",
    kind: "tool",
    label: "Practice a hard situation",
    blurb: "Rehearse a specific scenario with gated coaching before you face it for real.",
    icon: GraduationCap,
    route: "/tools/teacher-scenarios",
    triggers: [
      "practice scenario",
      "scenario practice",
      "rehearse",
      "role play",
      "role-play",
      "hard situation",
      "difficult situation",
      "what would i say",
      "how would i handle",
      "prepare for",
      "prep for",
      "parent is hostile",
      "student threw",
      "cell phone conflict",
      "burnout",
      "overwhelmed before",
    ],
    explicitAskTriggers: ["scenario practice", "practice a hard situation", "teacher scenarios"],
  },
  {
    id: "reset_my_nervous_system",
    sidebarNavItemId: "reset-nervous",
    kind: "chat",
    label: "Try Reset my Nervous System",
    blurb: "A short guided reset for when your body is running ahead of your thinking.",
    icon: Sparkles,
    chatEntry: "reset_my_nervous_system",
    triggers: [
      "anxious",
      "anxiety",
      "panicking",
      "panic",
      "racing heart",
      "can't breathe",
      "shaky",
      "shaking",
      "overwhelmed",
      "freaking out",
      "spiraling",
      "spiralling",
      "dysregulated",
      "ground myself",
      "calm down",
      "regulate",
      "fight or flight",
    ],
    explicitAskTriggers: ["reset my nervous system", "nervous system reset"],
    showInHubHero: true,
    hubChipOrder: 1,
    hubChipLabel: "Reset my nervous system",
    hubHintExample: "reset my nervous system",
  },
  {
    id: "reclaim_my_balance",
    sidebarNavItemId: "reclaim-balance",
    kind: "chat",
    label: "Try Reclaim my Balance",
    blurb: "Find the through-line when work and life are pulling in different directions.",
    icon: Scale,
    chatEntry: "reclaim_my_balance",
    triggers: [
      "burned out",
      "burnt out",
      "burnout",
      "exhausted",
      "drained",
      "no energy",
      "work life balance",
      "work-life balance",
      "boundaries",
      "boundary",
      "saying no",
      "stretched thin",
      "spread thin",
      "too much on my plate",
      "running on empty",
    ],
    explicitAskTriggers: ["reclaim my balance"],
    hubHintExample: "reclaim my balance",
  },
  {
    id: "expand_my_impact",
    sidebarNavItemId: "expand-impact",
    kind: "chat",
    label: "Try Expand my Impact",
    blurb: "Translate intent into the next concrete move that matters most.",
    icon: SunMedium,
    chatEntry: "expand_my_impact",
    triggers: [
      "expand my impact",
      "make a difference",
      "bigger impact",
      "more impact",
      "leverage",
      "scale up",
      "what's next for",
      "next chapter",
      "career growth",
      "stretch goal",
    ],
    explicitAskTriggers: ["expand my impact"],
    hubHintExample: "expand my impact",
  },
  {
    id: "connect_with_students",
    sidebarNavItemId: "lab-students",
    kind: "chat",
    label: "Practice with Students",
    blurb: "Rehearse a hard student conversation in a low-stakes chat space.",
    icon: Users,
    chatEntry: "connect_with_students",
    triggers: [
      "student",
      "students",
      "kids",
      "my kid",
      "my students",
      "classroom",
      "in class",
      "talk to a student",
      "conversation with a student",
      "hard conversation with",
      "difficult student",
      "shut down",
      "not engaging",
      "won't talk",
    ],
    explicitAskTriggers: [
      "practice with students",
      "connect with students",
      "connecting with students",
      "conversation lab students",
    ],
    showInHubHero: true,
    hubChipOrder: 2,
    hubChipLabel: "Practice a difficult conversation",
    hubHintExample: "conversation with a student",
  },
  {
    id: "strengthen_my_voice",
    sidebarNavItemId: "lab-parents",
    kind: "chat",
    label: "Practice with Parents",
    blurb: "Rehearse the parent conversation before the email or phone call.",
    icon: UserRound,
    chatEntry: "strengthen_my_voice",
    triggers: [
      "parent",
      "parents",
      "guardian",
      "guardians",
      "family",
      "families",
      "call home",
      "phone call home",
      "email home",
      "parent meeting",
      "parent conference",
      "parent teacher",
      "angry parent",
      "upset parent",
    ],
    explicitAskTriggers: ["practice with parents", "strengthen my voice", "parents conversation lab"],
    hubHintExample: "parent conference",
  },
  {
    id: "lead_with_clarity",
    sidebarNavItemId: "lab-colleagues",
    kind: "chat",
    label: "Practice with Colleagues",
    blurb: "Pre-rehearse the conversation with a peer, team member, or admin.",
    icon: MessagesSquare,
    chatEntry: "lead_with_clarity",
    triggers: [
      "colleague",
      "coworker",
      "co-worker",
      "co worker",
      "teammate",
      "team member",
      "department",
      "principal",
      "my admin",
      "supervisor",
      "boss",
      "staff meeting",
      "grade level team",
      "plc",
    ],
    explicitAskTriggers: ["practice with colleagues", "lead with clarity"],
    hubHintExample: "talk to a colleague",
  },
  {
    id: "get_coaching",
    sidebarNavItemId: "get-coaching",
    kind: "chat",
    label: "Talk to My Coach",
    blurb: "A longer, open coaching space when you want to think out loud.",
    icon: HeartPulse,
    chatEntry: "get_coaching",
    triggers: [
      "i need coaching",
      "want coaching",
      "talk to a coach",
      "talk to my coach",
      "coaching session",
      "long conversation",
      "deeper conversation",
      "process this",
      "process out loud",
    ],
    explicitAskTriggers: ["talk to my coach", "my coach", "get coaching"],
    hubHintExample: "talk to my coach",
  },
  {
    id: "renew_my_spark",
    sidebarNavItemId: "renew-spark",
    kind: "chat",
    label: "Try Renew my Spark",
    blurb: "Reconnect with what still lights you up when the work feels heavy.",
    icon: Flame,
    chatEntry: "renew_my_spark",
    triggers: [
      "renew my spark",
      "lost my spark",
      "lost my passion",
      "remember why i teach",
      "why i teach",
      "reignite",
      "feeling numb",
      "going through the motions",
      "joy is gone",
      "burnt out on teaching",
    ],
    explicitAskTriggers: ["renew my spark"],
    hubHintExample: "renew my spark",
  },
  {
    id: "navigate_parent_relationships",
    sidebarNavItemId: "lab-parents",
    kind: "chat",
    label: "Navigate Parent Relationships",
    blurb: "Prepare for high-stakes parent conversations with clarity and steadiness.",
    icon: UserRound,
    chatEntry: "navigate_parent_relationships",
    triggers: [
      "navigate parent",
      "parent relationships",
      "difficult parent",
      "parent escalation",
      "custody",
      "angry family",
      "board of parents",
      "community backlash",
      "parent complaint",
    ],
    explicitAskTriggers: [
      "navigate parent relationships",
      "parent relationships lab",
    ],
    hubHintExample: "navigate parent relationships",
  },
  {
    id: "leading_your_staff",
    sidebarNavItemId: "lab-colleagues",
    kind: "chat",
    label: "Leading Your Staff",
    blurb: "Rehearse the leadership conversation before you walk into the room.",
    icon: MessagesSquare,
    chatEntry: "leading_your_staff",
    triggers: [
      "leading my staff",
      "leading your staff",
      "staff conversation",
      "leadership conversation",
      "talk to my team",
      "faculty meeting",
      "hard conversation with staff",
      "underperforming teacher",
      "coaching a teacher",
    ],
    explicitAskTriggers: ["leading your staff", "lead my staff"],
    hubHintExample: "leading your staff",
  },
  {
    id: "navigate_board_relationships",
    sidebarNavItemId: "lab-board",
    kind: "chat",
    label: "Effective Board Communication",
    blurb: "Practice board-facing language before the meeting where it counts.",
    icon: Landmark,
    chatEntry: "navigate_board_relationships",
    triggers: [
      "school board",
      "board meeting",
      "board member",
      "present to the board",
      "board communication",
      "superintendent report",
      "governance",
      "board packet",
    ],
    explicitAskTriggers: [
      "effective board communication",
      "board communication",
      "conversation lab board",
    ],
    hubHintExample: "board meeting",
  },
  {
    id: "history",
    sidebarNavItemId: "history",
    kind: "tool",
    label: "Open History",
    blurb: "Find a past conversation and pick up where you left off.",
    icon: History,
    route: "/history",
    triggers: [
      "past chat",
      "past chats",
      "previous conversation",
      "previous chat",
      "resume a chat",
      "old conversation",
      "chat history",
      "where did we leave off",
    ],
    explicitAskTriggers: ["open history", "my history", "chat history"],
    hubHintExample: "open history",
  },
  {
    id: "insights",
    sidebarNavItemId: "insights",
    kind: "tool",
    label: "Open Insights",
    blurb: "See patterns and resources generated from your work in HeyBodhi.",
    icon: LineChart,
    route: "/growth/insights",
    triggers: [
      "my insights",
      "growth insights",
      "what patterns",
      "patterns in my work",
      "generated for me",
      "reflection insights",
    ],
    explicitAskTriggers: ["open insights", "my insights"],
    hubHintExample: "my insights",
  },
  {
    id: "resources",
    sidebarNavItemId: "resources",
    kind: "tool",
    label: "Open Resources",
    blurb: "Browse saved handouts, voice notes, and your resource library.",
    icon: BookOpen,
    route: "/resources",
    triggers: [
      "my resources",
      "saved handouts",
      "resource library",
      "personalized resources",
      "saved resources",
      "my library",
    ],
    explicitAskTriggers: ["open resources", "my resources"],
    hubHintExample: "my resources",
  },
  {
    id: "my_teams",
    sidebarNavItemId: "my-teams",
    kind: "tool",
    label: "Open My Teams",
    blurb: "View team coaching insights and support the educators you lead.",
    icon: UsersRound,
    route: "/admin/my-teams",
    triggers: [
      "my teams",
      "team dashboard",
      "coaching my staff",
      "team insights",
      "educators on my team",
      "building team",
    ],
    explicitAskTriggers: ["open my teams", "my teams"],
    hubHintExample: "my teams",
  },
  {
    id: "invite_teammates",
    sidebarNavItemId: "invite",
    kind: "tool",
    label: "Invite Teammates",
    blurb: "Bring colleagues onto HeyBodhi so they can use the same supports.",
    icon: UserPlus,
    route: "/admin/invite-teammates",
    triggers: [
      "invite teammate",
      "invite a colleague",
      "add a colleague",
      "invite staff",
      "onboard my team",
      "bring someone to bodhi",
      "share hey bodhi",
    ],
    explicitAskTriggers: ["invite teammates", "invite a teammate"],
    hubHintExample: "invite a teammate",
  },
];

const RECOMMENDATIONS_BY_ID: Record<string, ChatRecommendationDefinition> = Object.fromEntries(
  CHAT_RECOMMENDATIONS.map((r) => [r.id, r] as const)
);

export function getRecommendationById(id: string): ChatRecommendationDefinition | undefined {
  return RECOMMENDATIONS_BY_ID[id];
}

/**
 * Persona gating. Mirrors `getPersonaRestrictedRedirect` and
 * `isMentorEntryBlockedForPersona` so we never recommend a destination the
 * user can't actually open. Keep this list aligned with `workspacePersona.ts`.
 */
export function isRecommendationVisibleForPersona(
  rec: ChatRecommendationDefinition,
  persona: WorkspacePersona
): boolean {
  const isLeader = persona === "leader" || persona === "district_leader";

  if (isLeader && NON_LEADER_REC_IDS.has(rec.id)) return false;
  if (!isLeader && LEADER_ONLY_REC_IDS.has(rec.id)) return false;

  if (isLeader) return true;

  if (rec.kind === "tool") {
    // Tools gated for everyone except leader / district_leader.
    if (rec.id === "strategic_decision" && HUB_GATED_CHIP_IDS.has("decision")) return false;
    if (rec.id === "behavior_toolkit" && HUB_GATED_CHIP_IDS.has("incident")) return false;
  }

  if (rec.kind === "chat" && rec.chatEntry) {
    const effectiveEntry = resolveChatEntryForPersona(rec.chatEntry, persona);
    if (isMentorEntryBlockedForPersona(persona, effectiveEntry)) return false;
  }

  return true;
}
